const INVALID_RETURN_LITERALS = new Set(['null', 'undefined']);

const ALLOWED_HOME_QUERY_RETURNS = new Set([
  '/home?source=hitlab-recovery-email',
  '/home?source=hitlab-profile-ready',
]);

const getLocalUrl = (value) => {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return null;
  try {
    const parsed = new URL(raw, 'https://www.joegalaxy.net');
    return parsed.origin === 'https://www.joegalaxy.net' ? parsed : null;
  } catch (err) {
    return null;
  }
};

export const AUTH_RETURN_STORAGE_KEYS = [
  'jg:returnTo',
  'from',
  'originFrom',
];

const isHitLabArtistInviteCode = (value) => {
  const clean = String(value || '').trim();
  return /^[A-HJ-NP-Z2-9]{10,12}$/i.test(clean) ||
    /^[A-Za-z0-9._-]{40,2048}$/.test(clean);
};

export function isHitLabRadioArtistInviteReturnTo(value) {
  const parsed = getLocalUrl(value);
  if (!parsed || parsed.pathname !== '/hitlab/radio' || parsed.hash) {
    return false;
  }
  const keys = Array.from(parsed.searchParams.keys());
  if (
    keys.length !== 2 ||
    keys.some((key) => !['track', 'artistInvite'].includes(key))
  ) {
    return false;
  }
  const track = String(parsed.searchParams.get('track') || '').trim();
  const artistInvite = String(
    parsed.searchParams.get('artistInvite') || '',
  ).trim();
  return (
    /^[a-z0-9](?:[a-z0-9-]{0,118}[a-z0-9])?$/.test(track) &&
    isHitLabArtistInviteCode(artistInvite)
  );
}

export function isSafeAuthReturnTo(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (INVALID_RETURN_LITERALS.has(raw.toLowerCase())) return false;

  try {
    const parsed = getLocalUrl(raw);
    if (!parsed) return false;
    const pathWithSearch = `${parsed.pathname}${parsed.search}`;
    if (parsed.pathname === '/home') {
      return !parsed.hash && ALLOWED_HOME_QUERY_RETURNS.has(pathWithSearch);
    }
    if (parsed.pathname === '/hitlab/admin') return false;
    if (
      parsed.pathname === '/hitlab/radio' &&
      parsed.searchParams.has('artistInvite')
    ) {
      return isHitLabRadioArtistInviteReturnTo(raw);
    }
    return parsed.pathname !== '/login';
  } catch (err) {
    return false;
  }
}

export function firstSafeAuthReturnTo(...values) {
  return values.find((value) => isSafeAuthReturnTo(value)) || null;
}

export function getSafeAuthReturnToFromSearch(search = '') {
  const params = new URLSearchParams(search || '');
  return firstSafeAuthReturnTo(
    params.get('returnTo'),
    params.get('redirect'),
    params.get('from'),
  );
}

export function sanitizeStoredAuthReturnTo(storage = window.localStorage) {
  if (!storage) return null;
  let firstSafe = null;

  AUTH_RETURN_STORAGE_KEYS.forEach((key) => {
    const value = storage.getItem(key);
    if (!value) return;
    if (isSafeAuthReturnTo(value)) {
      if (!firstSafe) firstSafe = value;
      return;
    }
    storage.removeItem(key);
  });

  return firstSafe;
}

export function clearStoredAuthReturnTo(storage = null) {
  const target =
    storage || (typeof window !== 'undefined' ? window.localStorage : null);
  if (!target) return;

  AUTH_RETURN_STORAGE_KEYS.forEach((key) => {
    try {
      target.removeItem(key);
    } catch (err) {
      // Storage can be unavailable in privacy modes. Auth cleanup should continue.
    }
  });
}

export function getSupportedMagicReturnTo(value, fallback = '/playermap') {
  if (!isSafeAuthReturnTo(value)) return fallback;
  try {
    const parsed = new URL(value, 'https://www.joegalaxy.net');
    const pathWithSearch = `${parsed.pathname}${parsed.search || ''}`;
    if (ALLOWED_HOME_QUERY_RETURNS.has(pathWithSearch)) return pathWithSearch;
    if (parsed.pathname === '/localops/continue') return value;
    if (parsed.pathname === '/playermap') return value;
    if (
      /^\/profile\/[^/?#]+$/.test(parsed.pathname) &&
      !parsed.search &&
      !parsed.hash
    ) {
      return parsed.pathname;
    }
    if (isHitLabRadioArtistInviteReturnTo(value)) return pathWithSearch;
    if (
      parsed.pathname === '/hitlab/intake' ||
      parsed.pathname === '/hitlab/submit'
    ) {
      return `/localops/continue?returnTo=${encodeURIComponent(value)}`;
    }
  } catch (err) {
    return fallback;
  }
  return fallback;
}
